import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { QuickLink } from "models/propTypes/commonPropTypes";
import { AdvisorEnums } from "models/enums";
//#endregion
//#region components
import { useLocation, useNavigate } from "react-router-dom";
import { ActionLinksGroup } from "components/common";
import useQueryParamsHelper from "components/revisor/hooks/useQueryParamsHelper";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const RevisorShortcuts: React.FC = () => {
  const annualAccountsFinancialYear = useSelector(
    (store: AppState) => store.auth.selectedCompany.annualAccountsFinancialYear
  );

  const location = useLocation();
  const navigate = useNavigate();

  const { getFinancialYearRangeSearchParamString, getFinancialYearEndDateSearchParamString } =
    useQueryParamsHelper(annualAccountsFinancialYear);

  const onAccountsReceivableClick = () => {
    const searchParams = getFinancialYearEndDateSearchParamString(FormFieldConstants.Invoice.Filter.LedgerDate);
    navigate(UrlHelper.formatPath(`/Reports/Revenue/Invoices${searchParams}`), {
      state: { background: location },
    });
  };

  const onAccountingClick = () => {
    navigate(UrlHelper.formatPath("/Reports/Accounting"), {
      state: { background: location },
    });
  };

  const onHoldingRegisterClick = () => {
    const searchParams = getFinancialYearEndDateSearchParamString(
      FormFieldConstants.HoldingRegister.HoldingRegisterAssetsBaseFilter.Snapshot
    );
    navigate(UrlHelper.formatPath(`/Reports/HoldingRegisters${searchParams}`), {
      state: { background: location },
    });
  };

  const onSalariesClick = () => {
    const searchParams = `?reportType=${AdvisorEnums.SalariesReport.SalarySpecifications}`;
    navigate(UrlHelper.formatPath(`/Employees/Salaries${searchParams}`), {
      state: { background: location },
    });
  };

  const onIncomeStatementClick = () => {
    navigate(UrlHelper.formatPath(`/Reports/IncomeStatement${getFinancialYearRangeSearchParamString()}`), {
      state: { background: location },
    });
  };

  const onBalanceSheetClick = () => {
    navigate(UrlHelper.formatPath(`/Reports/BalanceSheet${getFinancialYearRangeSearchParamString()}`), {
      state: { background: location },
    });
  };

  const onAccountsPayableClick = () => {
    navigate(
      UrlHelper.formatPath(
        `/Reports/Expenses/IncomingInvoices${getFinancialYearEndDateSearchParamString(
          FormFieldConstants.IncomingInvoice.Filter.SnapshotDate
        )}`
      ),
      {
        state: { background: location },
      }
    );
  };

  const links: QuickLink[] = [
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AccountsReceivableLedger),
      onClickHandler: onAccountsReceivableClick,
    },
    {
      icon: IconDefinitionConstants.WCompany,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.HoldingRegister.HoldingRegister),
      onClickHandler: onHoldingRegisterClick,
    },
    {
      icon: IconDefinitionConstants.WMoneyBag,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.Salaries),
      onClickHandler: onSalariesClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accounting),
      onClickHandler: onAccountingClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.IncomeStatement),
      onClickHandler: onIncomeStatementClick,
      hidden: !annualAccountsFinancialYear,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.BalanceSheet),
      onClickHandler: onBalanceSheetClick,
      hidden: !annualAccountsFinancialYear,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.AccountsPayable),
      onClickHandler: onAccountsPayableClick,
    },
  ];

  return <ActionLinksGroup links={links} columns={{ xs: 12, sm: 6, md: 4, lg: 6, xl: 6 }} />;
};

export default RevisorShortcuts;
